// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2026-05-27 15:23:24 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163322 $
//  Purpose   : upload hooks
// =============================================================================
// ---Third-party imports
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import { dict } from 'ldash';
import log from 'uix/logging';
import { useActions, useCallback, useState, useSelector, useMemo, useEffect, useRef, usePartContext, useTestableState } from 'uix/hooks';
// ---Local imports
import * as UploadActions from './actions';
// ---Exports
export const useUpload = ()=>{
    let { name: partName, type: partType } = usePartContext();
    let testPartId = partName || partType || 'global';
    let { uploadFile, cancelUploads: dispatchCancelUploads } = useActions(UploadActions);
    let uploads = useSelector(Sel.uploads);
    let [uploadQueue, setUploadQueue] = useTestableState([], ()=>`upload.${testPartId}.queue`);
    let queueRef = useRef(uploadQueue);
    useEffect(()=>{
        queueRef.current = uploadQueue;
    }, [
        uploadQueue
    ]);
    let uploadFiles = useCallback((files)=>{
        files.forEach((f)=>uploadFile(f.path, f.bytes));
        setUploadQueue(files);
    });
    let cancelUploads = useCallback(()=>{
        let fileids = queueRef.current.map((f)=>f.path);
        dispatchCancelUploads(fileids);
        setUploadQueue([]);
    });
    let uploaded_done = useMemo(()=>uploadQueue.filter((f)=>uploads[f.path]?.state == 'upload done'), [
        uploads,
        uploadQueue
    ]);
    let uploaded_count = uploaded_done.length;
    let uploaded_bytes = useMemo(()=>uploadQueue.reduce((acc, f)=>{
            let u = uploads[f.path];
            if (!u) return acc;
            if (u.state == 'upload done') {
                return acc + (u.size || f.size || 0);
            }
            if (u.state == 'upload chunk' && u.chunks > 0) {
                return acc + Math.min(u.size || f.size || 0, (u.size || f.size || 0) * u.chunk / u.chunks);
            }
            return acc;
        }, 0), [
        uploads,
        uploadQueue
    ]);
    let earliest_start = useMemo(()=>{
        let started = uploadQueue.map((f)=>uploads[f.path]?.startedAt).filter(Boolean).sort()[0];
        return started || null;
    }, [
        uploads,
        uploadQueue
    ]);
    let progress = useMemo(()=>{
        let q = uploadQueue.filter((f)=>uploads[f.path]);
        let elapsedMs = earliest_start ? Math.max(1, Date.now() - earliest_start) : 0;
        let rateBps = elapsedMs ? uploaded_bytes * 1000 / elapsedMs : 0;
        return {
            total: q.length,
            step: uploaded_count,
            running: uploaded_count < q.length,
            rateBps
        };
    }, [
        uploadQueue,
        uploaded_count,
        uploaded_bytes,
        earliest_start
    ]);
    let activeUploads = uploadQueue.filter((f)=>uploads[f.path]?.state == 'upload chunk').map((f)=>({
            ...uploads[f.path],
            fpath: f.path
        }));
    progress = useMemo(()=>({
            ...progress,
            activeUploads
        }), [
        progress,
        activeUploads[0]?.chunk
    ]);
    //~ mapItems(uploads, (path, data) => [path])
    let uploaded = useMemo(()=>dict(uploaded_done.map(({ path })=>[
                path,
                uploads[path]?.uploadid
            ])), [
        uploaded_done,
        uploads
    ]);
    return {
        uploadFiles,
        cancelUploads,
        progress,
        uploaded
    };
};
